import axios from 'axios';

const BACKEND_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config (auth via secure HttpOnly cookie)
const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const PUBLIC_PATHS = ['/', '/login', '/register', '/register-company', '/register-admin', '/pricing', '/saas-pricing', '/forgot-password', '/reset-password', '/verify-ronde'];

// Handle response errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (!PUBLIC_PATHS.includes(window.location.pathname)) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (credentials) => apiClient.post('/auth/login', credentials),
  register: (companyData, userData) => apiClient.post('/auth/register', companyData, {
    params: {
      user_name: userData.name,
      user_email: userData.email,
      user_password: userData.password,
      user_phone: userData.phone
    }
  }),
  // registerAdmin n'existe pas côté backend pour le moment
  registerAdmin: (adminData) => Promise.reject(new Error('Création d\'administrateur non disponible. Contactez votre support.')),

  me: () => apiClient.get('/auth/me'),
  logout: () => apiClient.post('/auth/logout'),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => apiClient.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Sites APIs
export const sitesAPI = {
  getAll: (params = {}) => apiClient.get('/sites/', { params }),
  getById: (id) => apiClient.get(`/sites/${id}`),
  create: (data) => apiClient.post('/sites/', data),
  update: (id, data) => apiClient.put(`/sites/${id}`, data),
  delete: (id) => apiClient.delete(`/sites/${id}`),
  getStats: (id) => apiClient.get(`/sites/${id}/stats`),
};

// Agents APIs
export const agentsAPI = {
  getAll: (params = {}) => apiClient.get('/agents/', { params }),
  getById: (id) => apiClient.get(`/agents/${id}`),
  create: (data) => apiClient.post('/agents/', data),
  update: (id, data) => apiClient.put(`/agents/${id}`, data),
  delete: (id) => apiClient.delete(`/agents/${id}`),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/agents/${id}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Shifts APIs
export const shiftsAPI = {
  getAll: (params = {}) => apiClient.get('/shifts/', { params }),
  getById: (id) => apiClient.get(`/shifts/${id}`),
  create: (data) => apiClient.post('/shifts/', data),
  update: (id, data) => apiClient.put(`/shifts/${id}`, data),
  delete: (id) => apiClient.delete(`/shifts/${id}`),
  start: (id) => apiClient.post(`/shifts/${id}/start`),
  end: (id) => apiClient.post(`/shifts/${id}/end`),
  getWeekly: (startDate) => apiClient.get(`/shifts/weekly/${startDate}`),
};

// Cameras APIs
export const camerasAPI = {
  getAll: (params = {}) => apiClient.get('/cameras/', { params }),
  getById: (id) => apiClient.get(`/cameras/${id}`),
  create: (data) => apiClient.post('/cameras/', data),
  update: (id, data) => apiClient.put(`/cameras/${id}`, data),
  delete: (id) => apiClient.delete(`/cameras/${id}`),
  ping: (id) => apiClient.post(`/cameras/${id}/ping`),
  getBySite: (siteId) => apiClient.get(`/cameras/site/${siteId}`),
};

// Alarms APIs
export const alarmsAPI = {
  getAll: (params = {}) => apiClient.get('/alarms/', { params }),
  getById: (id) => apiClient.get(`/alarms/${id}`),
  create: (data) => apiClient.post('/alarms/', data),
  update: (id, data) => apiClient.put(`/alarms/${id}`, data),
  resolve: (id, resolution) => apiClient.post(`/alarms/${id}/resolve`, { resolution }),
  assign: (id, agentId) => apiClient.post(`/alarms/${id}/assign`, { agent_id: agentId }),
  getStats: (days = 30) => apiClient.get('/alarms/stats/summary', { params: { days } }),
};

// Reports APIs
export const reportsAPI = {
  getAll: () => apiClient.get('/reports/'),
  getById: (id) => apiClient.get(`/reports/${id}`),
  generate: (data) => apiClient.post('/reports/generate', data),
  download: (id) => apiClient.get(`/reports/${id}/download`, { responseType: 'blob' }),
  delete: (id) => apiClient.delete(`/reports/${id}`),
};

// Security Reports APIs
export const securityReportsAPI = {
  getAll: (params = {}) => apiClient.get('/security-reports/', { params }),
  getById: (id) => apiClient.get(`/security-reports/${id}`),
  create: (data) => apiClient.post('/security-reports', data),
  update: (id, data) => apiClient.put(`/security-reports/${id}`, data),
  delete: (id) => apiClient.delete(`/security-reports/${id}`),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/security-reports/${id}/upload-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadAudio: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/security-reports/${id}/upload-audio`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  exportPDF: (id) => apiClient.post(`/security-reports/${id}/export-pdf`),
  exportWord: (id) => apiClient.post(`/security-reports/${id}/export-word`),
  exportExcel: (id) => apiClient.post(`/security-reports/${id}/export-excel`),
};

// AI APIs
export const aiAPI = {
  chat: (message, context = null) => apiClient.post('/ai/chat', { message, context }),
  analyze: (type, data) => apiClient.post('/ai/analyze', { type, data }),
  getPredictions: () => apiClient.get('/ai/predictions'),
  getInsights: () => apiClient.get('/ai/insights'),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => apiClient.get('/health'), // For now, use health endpoint
};

// Registre Personnel APIs
export const registrePersonnelAPI = {
  getAll: () => apiClient.get('/registre-personnel/'),
  create: (data) => apiClient.post('/registre-personnel/', data),
  update: (id, data) => apiClient.put(`/registre-personnel/${id}`, data),
  delete: (id) => apiClient.delete(`/registre-personnel/${id}`),
};

// Pay slips (fiches de paie) APIs
export const paySlipsAPI = {
  generatePDF: (payload) => apiClient.post('/pay-slips/generate-pdf', payload, { responseType: 'blob' }),
};

// Facture PDF API
export const facturePdfAPI = {
  download: (factureId) => apiClient.get(`/facturation/factures/${factureId}/pdf`, { responseType: 'blob' }),
};

export const clientsAPI = {
  getAll: () => apiClient.get('/clients/'),
  create: (data) => apiClient.post('/clients/', data),
  update: (id, data) => apiClient.put(`/clients/${id}`, data),
  delete: (id) => apiClient.delete(`/clients/${id}`),
};

export default apiClient;